import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Polyline, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Package, Truck, MapPin, Loader2, Send, Check, X, Phone, ChevronRight,
  PlusCircle, CircleDot, Timer, ArrowRight,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLocation as useUserLocation } from "@/context/LocationContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

// Custom Leaflet markers — colored dots so we don't depend on external image assets.
const iconFor = (color) => L.divIcon({
  className: "",
  html: `<div style="width:20px;height:20px;border-radius:9999px;background:${color};border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,.3)"></div>`,
  iconAnchor: [10, 10],
});
const pickupIcon = iconFor("#059669");
const dropIcon   = iconFor("#dc2626");
const agentIcon  = iconFor("#E05E36");

const STATUS_META = {
  requested:  { label: "Requested",  color: "bg-blue-100 text-blue-800" },
  assigned:   { label: "Assigned",   color: "bg-amber-100 text-amber-800" },
  picked_up:  { label: "Picked up",  color: "bg-fuchsia-100 text-fuchsia-800" },
  in_transit: { label: "In transit", color: "bg-orange-100 text-orange-800" },
  delivered:  { label: "Delivered",  color: "bg-emerald-100 text-emerald-800" },
  cancelled:  { label: "Cancelled",  color: "bg-stone-200 text-stone-700" },
};

const relTime = (iso) => {
  if (!iso) return "";
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function Delivery() {
  const { id } = useParams();
  if (id) return <DeliveryDetail deliveryId={id} />;
  return <DeliveryList />;
}

// -------------------- List --------------------
function DeliveryList() {
  const { user } = useAuth();
  const [role, setRole] = useState("sender");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatorOpen, setCreatorOpen] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const r = await api.get(`/delivery/mine?role=${role}`);
      setItems(r.data || []);
    } catch {} finally { setLoading(false); }
  }, [role, user]);

  useEffect(() => { load(); }, [load]);

  if (!user) return <div className="p-12 text-center text-stone-500">Sign in to use delivery.</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 py-4" data-testid="delivery-list">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2"><Truck className="text-[#E05E36]" size={22} />
            <h1 className="heading text-2xl font-black text-stone-900">Delivery</h1>
          </div>
          <p className="text-xs text-stone-500 mt-0.5">Track parcels realtime with GPS pings + OTP delivery.</p>
        </div>
        <Button onClick={() => setCreatorOpen(true)} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="delivery-new-btn">
          <PlusCircle size={14} className="mr-1" /> Send parcel
        </Button>
      </div>

      <div className="mt-4 flex gap-2">
        {["sender", "agent"].map(r => (
          <button key={r} onClick={() => setRole(r)}
            className={`px-3 py-1.5 text-xs font-bold rounded-full ${role === r ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600"}`}
            data-testid={`delivery-role-${r}`}>
            {r === "sender" ? "I'm sending" : "I'm delivering"}
          </button>
        ))}
      </div>

      <div className="mt-4">
        {loading ? <div className="text-center py-16"><Loader2 className="inline animate-spin" /></div>
        : items.length === 0 ? (
          <div className="text-center py-16 text-stone-500">
            <Package className="mx-auto mb-2 text-stone-300" size={32} />
            {role === "sender" ? "No parcels sent yet." : "Nothing assigned to you yet."}
          </div>
        ) : (
          <div className="space-y-2">
            {items.map(d => {
              const s = STATUS_META[d.status] || {};
              return (
                <Link key={d.delivery_id} to={`/delivery/${d.delivery_id}`}
                  className="block bg-white border border-stone-200 rounded-2xl p-3 hover:bg-stone-50"
                  data-testid={`delivery-row-${d.delivery_id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded ${s.color}`}>{s.label}</span>
                    <span className="text-[10px] text-stone-500">{d.distance_km}km · ETA {d.eta_minutes}m</span>
                  </div>
                  <div className="text-sm font-bold text-stone-900">→ {d.recipient_name}</div>
                  <div className="text-xs text-stone-500 line-clamp-1">
                    <MapPin size={9} className="inline mr-0.5" />{d.pickup?.address} <ArrowRight size={9} className="inline mx-1" /> {d.dropoff?.address}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {creatorOpen && <DeliveryCreator onClose={() => setCreatorOpen(false)} onCreated={() => { setCreatorOpen(false); load(); }} />}
    </div>
  );
}

// -------------------- Creator --------------------
function DeliveryCreator({ onClose, onCreated }) {
  const { loc } = useUserLocation();
  const [f, setF] = useState({
    recipient_name: "", recipient_phone: "",
    pickup_address: loc?.city || "", pickup_lat: loc?.lat, pickup_lng: loc?.lng,
    dropoff_address: "", dropoff_lat: null, dropoff_lng: null,
    package_desc: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!f.recipient_name || !f.recipient_phone || !f.pickup_address || !f.dropoff_address) {
      toast.error("Fill all fields"); return;
    }
    if (!(f.pickup_lat && f.pickup_lng)) { toast.error("Pickup GPS missing — enable location"); return; }
    // If drop-off coords are missing, fake with a nearby offset (real app would geocode).
    const dLat = f.dropoff_lat ?? f.pickup_lat + 0.01;
    const dLng = f.dropoff_lng ?? f.pickup_lng + 0.01;
    setBusy(true);
    try {
      await api.post("/delivery", {
        ...f, dropoff_lat: dLat, dropoff_lng: dLng,
        city: loc?.city, order_id: null,
      });
      toast.success("Delivery requested");
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 grid place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-md p-5 space-y-2" data-testid="delivery-creator">
        <div className="font-bold text-lg">Send a parcel</div>
        <Input placeholder="Recipient name" value={f.recipient_name} onChange={e => setF({ ...f, recipient_name: e.target.value })} data-testid="delivery-recipient-name" />
        <Input placeholder="Recipient phone" value={f.recipient_phone} onChange={e => setF({ ...f, recipient_phone: e.target.value.replace(/\D/g, "") })} data-testid="delivery-recipient-phone" />
        <Input placeholder="Pickup address" value={f.pickup_address} onChange={e => setF({ ...f, pickup_address: e.target.value })} data-testid="delivery-pickup" />
        <Input placeholder="Drop-off address" value={f.dropoff_address} onChange={e => setF({ ...f, dropoff_address: e.target.value })} data-testid="delivery-dropoff" />
        <Textarea placeholder="Package description (optional)" rows={2} value={f.package_desc} onChange={e => setF({ ...f, package_desc: e.target.value })} />
        <div className="text-[11px] text-stone-500 inline-flex items-center gap-1">
          <MapPin size={10} /> Pickup: {f.pickup_lat ? `${f.pickup_lat.toFixed(3)}, ${f.pickup_lng.toFixed(3)}` : "GPS required"}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="delivery-submit">
            {busy ? <Loader2 className="animate-spin" size={14} /> : "Send"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// -------------------- Detail (live tracking) --------------------
function DeliveryDetail({ deliveryId }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [trail, setTrail] = useState([]);
  const [busy, setBusy] = useState(false);
  const [otp, setOtp] = useState("");
  const wsRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [d, t] = await Promise.all([
        api.get(`/delivery/${deliveryId}`),
        api.get(`/delivery/${deliveryId}/trail`).catch(() => ({ data: { pings: [] } })),
      ]);
      setDoc(d.data); setTrail(t.data.pings || []);
    } catch { toast.error("Delivery not found"); nav("/delivery"); }
  }, [deliveryId, nav]);
  useEffect(() => { load(); }, [load]);

  // 10s polling fallback + WebSocket push for ping updates.
  useEffect(() => {
    const iv = setInterval(load, 10_000);
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    let ws;
    try {
      ws = new WebSocket(backend.replace(/^http/, "ws") + "/api/messenger/ws");
      ws.onmessage = (ev) => {
        try {
          const m = JSON.parse(ev.data);
          if (m.type === "delivery_ping" && m.delivery_id === deliveryId) {
            setDoc(d => d ? ({ ...d, agent_current: m.ping }) : d);
            setTrail(t => [...t, m.ping].slice(-200));
          }
        } catch {}
      };
    } catch {}
    return () => { clearInterval(iv); try { ws?.close(); } catch {} };
  }, [deliveryId, load]);

  const iAmAgent = doc && user && doc.agent_id === user.user_id;
  const iAmSender = doc && user && doc.sender_id === user.user_id;

  const assign = async () => {
    setBusy(true);
    try { await api.post(`/delivery/${deliveryId}/assign`); toast.success("Assigned"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const advance = async (next) => {
    setBusy(true);
    try { await api.post(`/delivery/${deliveryId}/status`, { status: next }); toast.success(`→ ${STATUS_META[next]?.label || next}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const deliverWithOtp = async () => {
    if (!/^\d{4}$/.test(otp)) { toast.error("4-digit OTP"); return; }
    setBusy(true);
    try { await api.post(`/delivery/${deliveryId}/deliver`, { otp }); toast.success("Delivered ✓"); load(); }
    catch (e) { const d = e?.response?.data?.detail; toast.error(d?.message || (typeof d === "string" ? d : "Wrong OTP")); }
    finally { setBusy(false); }
  };

  // Agent-side realtime GPS pings — every 12s while status is picked_up/in_transit.
  useEffect(() => {
    if (!iAmAgent || !doc) return;
    if (!["picked_up", "in_transit"].includes(doc.status)) return;
    if (!navigator.geolocation) return;
    const iv = setInterval(() => {
      navigator.geolocation.getCurrentPosition(async (p) => {
        try { await api.post(`/delivery/${deliveryId}/ping`, { lat: p.coords.latitude, lng: p.coords.longitude, speed_kmh: (p.coords.speed || 0) * 3.6 }); }
        catch {}
      }, () => {}, { enableHighAccuracy: true, maximumAge: 10000 });
    }, 12_000);
    return () => clearInterval(iv);
  }, [iAmAgent, doc, deliveryId]);

  if (!doc) return <div className="p-12 text-center"><Loader2 className="inline animate-spin" /></div>;

  const pk = doc.pickup || { lat: 19.076, lng: 72.8777 };
  const dp = doc.dropoff || pk;
  const center = doc.agent_current
    ? [doc.agent_current.lat, doc.agent_current.lng]
    : [(pk.lat + dp.lat) / 2, (pk.lng + dp.lng) / 2];
  const polyline = trail.length > 1
    ? trail.map(p => [p.lat, p.lng])
    : [[pk.lat, pk.lng], [dp.lat, dp.lng]];
  const st = STATUS_META[doc.status];

  return (
    <div className="max-w-3xl mx-auto px-4 py-4" data-testid="delivery-detail">
      <Link to="/delivery" className="text-xs text-stone-500 hover:text-stone-900">← All deliveries</Link>

      <div className="mt-2 bg-white border border-stone-200 rounded-3xl overflow-hidden">
        <div style={{ height: 320 }}>
          <MapContainer center={center} zoom={13} style={{ height: "100%", width: "100%" }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
            {doc.pickup && <Marker position={[pk.lat, pk.lng]} icon={pickupIcon}><Popup>Pickup</Popup></Marker>}
            {doc.dropoff && <Marker position={[dp.lat, dp.lng]} icon={dropIcon}><Popup>Drop-off</Popup></Marker>}
            {doc.agent_current && (
              <Marker position={[doc.agent_current.lat, doc.agent_current.lng]} icon={agentIcon}>
                <Popup>Agent · {relTime(doc.agent_current.at)}</Popup>
              </Marker>
            )}
            <Polyline positions={polyline} color="#E05E36" weight={3} dashArray="6" />
          </MapContainer>
        </div>

        <div className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] text-stone-500 font-mono">{doc.delivery_id}</div>
              <div className="font-bold text-sm">→ {doc.recipient_name} · {doc.recipient_phone}</div>
            </div>
            <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded ${st.color}`}>{st.label}</span>
          </div>

          <div className="text-xs text-stone-500">
            <div><CircleDot size={10} className="inline mr-1 text-emerald-600" /> {doc.pickup?.address}</div>
            <div><CircleDot size={10} className="inline mr-1 text-red-600" /> {doc.dropoff?.address}</div>
            <div><Timer size={10} className="inline mr-1" /> {doc.distance_km} km · ETA {doc.eta_minutes} min</div>
          </div>

          {iAmSender && doc.status === "requested" && (
            <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg p-2 text-amber-800">
              Waiting for an agent to accept. Share the link with a friend / delivery partner.
            </div>
          )}
          {iAmSender && doc.share_token && doc.status !== "delivered" && doc.status !== "cancelled" && (
            <div className="text-xs bg-sky-50 border border-sky-200 rounded-lg p-2 text-sky-900" data-testid="delivery-share-block">
              <div className="font-bold mb-1">Live tracking link for your recipient</div>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={`${window.location.origin}/track/delivery/${doc.delivery_id}?token=${doc.share_token}`}
                  className="flex-1 rounded px-2 py-1 bg-white border border-sky-200 text-[11px] font-mono truncate"
                  onFocus={(e) => e.target.select()}
                  data-testid="delivery-share-input"
                />
                <button
                  onClick={() => {
                    const url = `${window.location.origin}/track/delivery/${doc.delivery_id}?token=${doc.share_token}`;
                    navigator.clipboard?.writeText(url).then(() => toast.success("Link copied"), () => toast.error("Copy failed"));
                  }}
                  className="bg-sky-600 hover:bg-sky-700 text-white rounded px-3 text-[11px] font-bold"
                  data-testid="delivery-share-copy"
                >
                  Copy
                </button>
              </div>
            </div>
          )}
          {iAmSender && doc.otp_delivery && doc.status !== "delivered" && (
            <div className="text-xs bg-emerald-50 border border-emerald-200 rounded-lg p-2 text-emerald-900">
              Delivery OTP: <b className="text-lg font-mono">{doc.otp_delivery}</b> — share with the recipient only.
            </div>
          )}

          {/* Agent actions */}
          {iAmAgent && (
            <div className="space-y-2">
              {doc.status === "assigned" && (
                <Button onClick={() => advance("picked_up")} disabled={busy} className="w-full rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="delivery-picked-up">
                  Mark picked up
                </Button>
              )}
              {doc.status === "picked_up" && (
                <Button onClick={() => advance("in_transit")} disabled={busy} className="w-full rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="delivery-in-transit">
                  Start transit (GPS on)
                </Button>
              )}
              {doc.status === "in_transit" && (
                <div className="flex gap-2">
                  <Input value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="Delivery OTP" data-testid="delivery-otp" />
                  <Button onClick={deliverWithOtp} disabled={busy} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="delivery-deliver">
                    Deliver
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Assign CTA (any authed non-sender) */}
          {doc.status === "requested" && !iAmSender && (
            <Button onClick={assign} disabled={busy} className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="delivery-assign">
              Assign me as agent
            </Button>
          )}

          {/* Timeline */}
          <div className="border-t border-stone-100 pt-3">
            <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 mb-1">Timeline</div>
            <ul className="text-xs space-y-0.5">
              {doc.status_timeline?.map((t, i) => (
                <li key={i} className="flex items-center gap-2 text-stone-600">
                  <Check size={11} className="text-emerald-500" />
                  <span className="font-bold">{STATUS_META[t.status]?.label || t.status}</span>
                  <span className="text-stone-400">· {relTime(t.at)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
